import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {fetchOrders} from '../../http/ordersAPI';
import SpinnerDark from '../UI/SpinnerDark';
import OrderCard from './OrderCard';
import {Context} from '../../index';
import {observer} from 'mobx-react-lite';
import {
    DEFAULT_PAGE_SIZE,
    normalizeOrderStatusCode,
    ORDER_DELIVERY_YANDEX_PICKUP,
    orderDeliveryPresentation,
    orderStatusPresentation,
    ORDERS_ROUTE,
} from '../../util/Constants';
import {formatOrderDate, orderDateTimeMs} from '../../util/formatOrderDate';

const FETCH_SIZE = 120;

function isActiveOrder(order) {
    const s = normalizeOrderStatusCode(order.status);
    return s !== 'CANCELED' && s !== 'DELIVERED';
}

function isHistoryOrder(order) {
    const s = normalizeOrderStatusCode(order.status);
    return s === 'CANCELED' || s === 'DELIVERED';
}

const Orders = observer(() => {
    const {orders: ordersStore} = useContext(Context);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [historyPage, setHistoryPage] = useState(0);
    const [rawList, setRawList] = useState([]);

    useEffect(() => {
        setIsLoading(true);
        fetchOrders({
            page: 0,
            size: FETCH_SIZE,
            sort: 'dateTime,desc',
        })
            .then((response) => {
                const list = response.orders || [];
                setRawList(list);
                ordersStore.setOrders(list);
            })
            .catch((error) => alert(error.message))
            .finally(() => setIsLoading(false));
    }, [ordersStore]);

    const activeOrders = useMemo(
        () =>
            rawList
                .filter(isActiveOrder)
                .sort((a, b) => orderDateTimeMs(b.dateTime) - orderDateTimeMs(a.dateTime)),
        [rawList],
    );

    const historyOrders = useMemo(
        () =>
            rawList
                .filter(isHistoryOrder)
                .sort((a, b) => orderDateTimeMs(b.dateTime) - orderDateTimeMs(a.dateTime)),
        [rawList],
    );

    const historyTotalPages = Math.max(1, Math.ceil(historyOrders.length / DEFAULT_PAGE_SIZE));

    useEffect(() => {
        setHistoryPage((p) => Math.min(p, historyTotalPages - 1));
    }, [historyTotalPages]);

    const historySlice = useMemo(() => {
        const start = historyPage * DEFAULT_PAGE_SIZE;
        return historyOrders.slice(start, start + DEFAULT_PAGE_SIZE);
    }, [historyOrders, historyPage]);

    const tableWrap = (children) => (
        <div className="overflow-x-auto rounded-2xl border border-stone-200/70">
            <table className="min-w-full divide-y divide-stone-200 text-sm">
                {children}
            </table>
        </div>
    );

    if (isLoading) {
        return <SpinnerDark/>;
    }

    return (
        <div className="space-y-10">
            <div className="lut-card">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-2xl font-semibold text-bark-900">
                        Активные заказы
                        <span className="ml-2 inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-pine-100 px-2 text-sm font-medium text-pine-900">
                            {activeOrders.length}
                        </span>
                    </h1>
                </div>
                <div className="mt-6 flex flex-wrap gap-4">
                    {activeOrders.length === 0 ? (
                        <p className="text-bark-500">Активных заказов нет</p>
                    ) : (
                        activeOrders.map((order) => (
                            <OrderCard
                                key={order.id}
                                id={order.id}
                                number={order.number}
                                dateTime={order.dateTime}
                                items={order.items}
                                price={order.price}
                                status={order.status}
                                shipment={order.shipmentResponse}
                                deliveryType={order.deliveryType}
                                deliveryFee={order.deliveryFee}
                            />
                        ))
                    )}
                </div>
            </div>

            <div className="lut-card">
                <h2 className="text-xl font-semibold text-bark-900">
                    История заказов
                    <span className="ml-2 inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-stone-200 px-2 text-sm font-medium text-bark-800">
                        {historyOrders.length}
                    </span>
                </h2>
                {tableWrap(
                    <>
                        <thead className="bg-cream-100/80 text-left text-xs font-semibold uppercase tracking-wide text-bark-600">
                            <tr>
                                <th className="px-4 py-3">Номер</th>
                                <th className="px-4 py-3">Дата</th>
                                <th className="px-4 py-3">Доставка</th>
                                <th className="px-4 py-3">Сумма</th>
                                <th className="px-4 py-3">Статус</th>
                                <th className="px-4 py-3">Действия</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100 bg-white">
                            {historySlice.length === 0 ? (
                                <tr>
                                    <td className="px-4 py-8 text-center text-bark-500" colSpan={6}>
                                        Завершённых и отменённых заказов пока нет.
                                    </td>
                                </tr>
                            ) : (
                                historySlice.map((order, idx) => {
                                    const st = orderStatusPresentation(order.status);
                                    const dtp = orderDeliveryPresentation(order.deliveryType);
                                    const dFee =
                                        order.deliveryType === ORDER_DELIVERY_YANDEX_PICKUP &&
                                        Number(order.deliveryFee) > 0
                                            ? `${order.deliveryFee} ₽`
                                            : '';
                                    return (
                                        <tr key={order.id ?? `hist-${idx}`}>
                                            <td className="whitespace-nowrap px-4 py-3 font-mono text-bark-800">
                                                {order.number}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-bark-600">
                                                {formatOrderDate(order.dateTime)}
                                            </td>
                                            <td className="max-w-[8rem] px-4 py-3 text-sm text-bark-700">
                                                {order.deliveryType ? (
                                                    <>
                                                        {dtp.short}
                                                        {dFee ? (
                                                            <span className="mt-0.5 block text-xs text-bark-500">
                                                                {dFee}
                                                            </span>
                                                        ) : null}
                                                    </>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 tabular-nums">
                                                {order.price} ₽
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={st.tw}>{st.translation}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`${ORDERS_ROUTE}/${order.id}`)}
                                                    className="text-sm font-medium text-pine-700 hover:underline"
                                                >
                                                    Подробнее
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </>,
                )}
                {historyTotalPages > 1 && (
                    <div className="mt-6 flex justify-center gap-3">
                        <button
                            type="button"
                            disabled={historyPage <= 0}
                            onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                            className="lut-btn-ghost disabled:opacity-40"
                        >
                            Назад
                        </button>
                        <span className="flex items-center text-sm text-bark-600">
                            {historyPage + 1} / {historyTotalPages}
                        </span>
                        <button
                            type="button"
                            disabled={historyPage >= historyTotalPages - 1}
                            onClick={() => setHistoryPage((p) => p + 1)}
                            className="lut-btn-ghost disabled:opacity-40"
                        >
                            Вперёд
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

export default Orders;
